import React, { ReactNode, useEffect } from "react";
import apiInterceptor, { setTokenRefreshHandler } from "./apiInterceptor";
import { useAuth } from "hooks‬/Auth";
import { useAuth0 } from "@auth0/auth0-react";
import { authorizationParams } from "constant/commonConstant";

const AxiosInterceptor = ({ children }: { children: ReactNode }) => {
  const { systemLogout, setIsLoadingPrivileges } = useAuth();
  const { getAccessTokenSilently } = useAuth0();

  const refreshTokenForUrl = async (url: string): Promise<string> => {
    try {
      setIsLoadingPrivileges(true);

      const accessToken = await getAccessTokenSilently({
        cacheMode: "off",
        authorizationParams: authorizationParams,
      });
      setIsLoadingPrivileges(false);
      return accessToken;
    } catch (error) {
      console.error("Token refresh failed:", error);
      setIsLoadingPrivileges(false);
      throw error;
    }
  };

  useEffect(() => {
    // Set the token refresh handler for apiInterceptor to use
    setTokenRefreshHandler(refreshTokenForUrl);

    // Response interceptor to handle 401 errors as fallback
    const responseInterceptor = apiInterceptor.interceptors.response.use(
      (response) => response,
      async (error: any) => {
        const originalRequest = error.config;

        if (
          error.response &&
          error.response.status === 401 &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          try {
            const newToken = await refreshTokenForUrl(originalRequest.url);

            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return apiInterceptor(originalRequest);
            } else {
              systemLogout();
              return Promise.reject(
                new Error("No token received during refresh")
              );
            }
          } catch (refreshError) {
            console.error("Token refresh failed on 401:", refreshError);
            systemLogout();
            return Promise.reject(new Error("Token refresh failed"));
          }
        }

        return Promise.reject(new Error("Token refresh failed"));
      }
    );

    return () => {
      apiInterceptor.interceptors.response.eject(responseInterceptor);
      // Clear the token refresh handler when component unmounts
      setTokenRefreshHandler(() =>
        Promise.reject(new Error("Token refresh handler not available"))
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemLogout, getAccessTokenSilently]);

  return <>{children}</>;
};

export default AxiosInterceptor;
